import { useEffect, useState } from "react";
import {
  TrendingUp, FileText, Truck, PackageCheck, Store, AlertTriangle, Loader2, ChevronRight, Download, ArrowUp, ArrowDown,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { api } from "@/lib/api";
import { rupiah, rupiahShort, fmtDate } from "@/lib/format";
import { Modal } from "@/components/Modal";
import { toast } from "sonner";

export default function Dashboard({ goto }) {
  const [period, setPeriod] = useState(30);
  const [oaDays, setOaDays] = useState(45);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dormantOpen, setDormantOpen] = useState(false);
  const [dormantAll, setDormantAll] = useState([]);
  const isoDate = (d) => d.toISOString().slice(0, 10);
  const [mx, setMx] = useState({
    start: isoDate(new Date(Date.now() - 29 * 86400000)),
    end: isoDate(new Date()), group_by: "type", metric: "amount",
  });
  const [exporting, setExporting] = useState(false);
  const exportMatrix = async () => {
    setExporting(true);
    try {
      const r = await api.get("/reports/sales-matrix.pdf", { params: mx, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `sales-matrix-${mx.start}_${mx.end}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("PDF Sales Matrix diunduh");
    } catch (e) { toast.error("Gagal ekspor PDF"); }
    finally { setExporting(false); }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/dashboard?period=${period}&oa_days=${oaDays}`).then((r) => {
      if (alive) { setData(r.data.data); setLoading(false); }
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period, oaDays]);

  const openDormant = async () => {
    setDormantOpen(true);
    try { const r = await api.get("/customers/dormant", { params: { oa_days: oaDays } }); setDormantAll(r.data.data); } catch {}
  };

  if (loading || !data) {
    return <div className="rj-loading" data-testid="dashboard-loading"><Loader2 className="spin" /> Memuat ringkasan…</div>;
  }

  const seg = data.sales_by_type || {};
  const metrics = [
    { key: "sales", label: "Total Sales", value: rupiah(data.total_sales), icon: TrendingUp, sub: `${data.total_order_count} order` },
    { key: "quote", label: "Total Quotation", value: data.total_quotation, icon: FileText, sub: "menunggu proses" },
    { key: "proc", label: "Order Diproses", value: rupiah(data.processing_amount), icon: Truck, sub: "sedang berjalan" },
    { key: "deliv", label: "Order Terkirim", value: rupiah(data.delivered_amount), icon: PackageCheck, sub: "delivered" },
    { key: "oa", label: "Outlet Aktif (OA)", value: data.total_oa, icon: Store, sub: `dari ${data.oa_total_direct} outlet direct` },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="section-head">
        <div>
          <p className="eyebrow">RINGKASAN</p>
          <h2>Ringkasan Penjualan</h2>
          <p>Metrik penjualan Rasa Juara dari data operasional.</p>
        </div>
        <div className="period-toggle" data-testid="period-toggle">
          <button className={period === 7 ? "active" : ""} onClick={() => setPeriod(7)} data-testid="period-7">7 Hari</button>
          <button className={period === 30 ? "active" : ""} onClick={() => setPeriod(30)} data-testid="period-30">30 Hari</button>
        </div>
      </div>

      <div className="metrics">
        {metrics.map((m) => {
          const Icon = m.icon;
          return (
            <div className="metric-card" key={m.label} data-testid={`metric-${m.label}`}>
              <div className="metric-icon"><Icon /></div>
              <span>{m.label}</span>
              <strong>{m.value}</strong>
              {m.key === "oa" ? (
                <>
                  <div className="oa-foot">
                    <span className={`oa-trend ${data.oa_trend > 0 ? "up" : data.oa_trend < 0 ? "down" : ""}`} data-testid="oa-trend">
                      {data.oa_trend > 0 ? <ArrowUp size={12} /> : data.oa_trend < 0 ? <ArrowDown size={12} /> : null}
                      {data.oa_trend > 0 ? `+${data.oa_trend}` : data.oa_trend}
                      <i>vs {oaDays} hr sblm</i>
                    </span>
                    <select className="oa-days-select" value={oaDays} onChange={(e) => setOaDays(Number(e.target.value))} data-testid="oa-days-select">
                      <option value={30}>30 hari</option>
                      <option value={45}>45 hari</option>
                      <option value={60}>60 hari</option>
                    </select>
                  </div>
                  <small className="metric-detail">{m.sub}</small>
                </>
              ) : (
                <small className="metric-detail">{m.sub}</small>
              )}
            </div>
          );
        })}
      </div>

      <div className="segment-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        {[
          { k: "Distributor", c: "#c50000" },
          { k: "MT", c: "#326aa9" },
          { k: "GT", c: "#ad6411" },
        ].map((s) => (
          <div className="segment-card" key={s.k} style={{ "--segment": s.c }} data-testid={`segment-${s.k}`}>
            <i />
            <div>
              <span>Sales {s.k}</span>
              <strong>{rupiahShort(seg[s.k] || 0)}</strong>
              <small>{rupiah(seg[s.k] || 0)}</small>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        <div className="panel">
          <div className="panel-head">
            <div>
              <h3>Tren Penjualan {period} Hari</h3>
              <p>Nilai order harian (rupiah)</p>
            </div>
            <div className="legend"><span><i className="actual" /> Aktual</span></div>
          </div>
          <div className="chart-wrap" style={{ minHeight: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chart} margin={{ top: 10, right: 6, left: 6, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eee5e2" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#918984" }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
                <Tooltip
                  formatter={(v) => rupiah(v)}
                  labelFormatter={(l) => fmtDate(l)}
                  contentStyle={{ borderRadius: 12, border: "1px solid #e8e2df", fontSize: 12 }}
                />
                <Bar dataKey="actual" radius={[6, 6, 0, 0]}>
                  {data.chart.map((_, i) => <Cell key={i} fill="#ff0200" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="panel priorities" data-testid="dormant-panel">
          <div className="panel-head">
            <div>
              <h3>Outlet Paling Lama Tidak Order</h3>
              <p>Outlet direct tanpa order &gt; {oaDays} hari</p>
            </div>
            <button onClick={openDormant} data-testid="dormant-view-all">Lihat semua ({data.dormant_count})</button>
          </div>
          {data.dormant_outlets.length === 0 ? (
            <div className="rj-empty"><Store /> <p>Tidak ada outlet dormant.</p></div>
          ) : data.dormant_outlets.map((o) => (
            <button key={o.odoo_customer_id} className="dormant-row" onClick={openDormant} data-testid={`dormant-${o.odoo_customer_id}`}>
              <span className={`priority-icon ${o.days_since > 60 ? "red" : "amber"}`}><AlertTriangle /></span>
              <span className="dormant-info">
                <strong>{o.name}</strong>
                <span>{o.area} · terakhir order {o.last_order_at ? fmtDate(o.last_order_at) : "belum pernah"}</span>
              </span>
              <b className={`dormant-days ${o.days_since > 60 ? "crit" : "warn"}`}>{o.days_since}h</b>
            </button>
          ))}
        </div>
      </div>

      <div className="lower-grid">
        <div className="panel activity">
          <div className="panel-head">
            <div><h3>Order Terbaru</h3><p>Aktivitas penjualan terkini</p></div>
            <button onClick={() => goto?.("sales")}>Ke Sales Order</button>
          </div>
          {data.recent_orders.length === 0 ? (
            <div className="rj-empty"><FileText /><p>Belum ada order.</p></div>
          ) : data.recent_orders.map((o) => (
            <div className="activity-row" key={o.id}>
              <div className="store-icon"><Store /></div>
              <div>
                <strong>{o.outlet}</strong>
                <span>{o.so_number} · {o.customer_type} · {fmtDate(o.created_at)}</span>
              </div>
              <div className="activity-amount">
                <strong>{rupiahShort(o.amount)}</strong>
                <span className={`order-status ${(o.status || "").toLowerCase().replace(/\s+/g, "-")}`}>{o.status}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="panel">
          <div className="panel-head"><div><h3>Sales per Tipe Customer</h3><p>Kontribusi periode {period} hari</p></div></div>
          <div style={{ marginTop: 8 }}>
            {["Distributor", "MT", "GT"].map((t) => {
              const total = (seg.Distributor || 0) + (seg.MT || 0) + (seg.GT || 0) || 1;
              const pct = Math.round(((seg[t] || 0) / total) * 100);
              return (
                <div className="territory" key={t} style={{ marginBottom: 12 }}>
                  <span style={{ fontWeight: 700 }}>{t}</span>
                  <div style={{ background: "#f0e8e5", borderRadius: 6, height: 8, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "#044f31" }} />
                  </div>
                  <b>{pct}%</b>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="panel" data-testid="sales-matrix-panel" style={{ marginTop: 18 }}>
        <div className="panel-head">
          <div><h3>Sales Matrix (PDF)</h3><p>Ekspor matriks penjualan per rentang tanggal</p></div>
        </div>
        <div className="matrix-controls">
          <label>Dari<input type="date" value={mx.start} onChange={(e) => setMx({ ...mx, start: e.target.value })} data-testid="matrix-start" /></label>
          <label>Sampai<input type="date" value={mx.end} onChange={(e) => setMx({ ...mx, end: e.target.value })} data-testid="matrix-end" /></label>
          <label>Kelompokkan<select value={mx.group_by} onChange={(e) => setMx({ ...mx, group_by: e.target.value })} data-testid="matrix-groupby">
            <option value="type">Tipe Customer</option>
            <option value="outlet">Outlet</option>
            <option value="city">Kota</option>
            <option value="owner">Sales / Owner</option>
          </select></label>
          <label>Metrik<select value={mx.metric} onChange={(e) => setMx({ ...mx, metric: e.target.value })} data-testid="matrix-metric">
            <option value="amount">Nilai (Rp)</option>
            <option value="qty">Qty</option>
          </select></label>
          <button className="rj-btn rj-btn-primary" onClick={exportMatrix} disabled={exporting} data-testid="matrix-export-btn">
            {exporting ? <Loader2 className="spin" size={15} /> : <Download size={15} />} Export PDF
          </button>
        </div>
      </div>

      <Modal open={dormantOpen} onClose={() => setDormantOpen(false)} className="dormant-customer-dialog"
        title="Outlet Dormant" subtitle="Outlet direct tanpa order lebih dari 30 hari" testId="dormant-dialog">
        <div className="dormant-dialog-list">
          {dormantAll.length === 0 ? <p className="coverage-none">Tidak ada data dormant.</p> :
            dormantAll.map((o) => (
              <div key={o.odoo_customer_id} className={o.days_since > 60 ? "critical" : ""}>
                <span><strong>{o.name}</strong><small>{o.area} · {o.address || "-"}</small></span>
                <span><small>Order terakhir</small><strong style={{ fontSize: 12 }}>{o.last_order_at ? fmtDate(o.last_order_at) : "Belum pernah"}</strong></span>
                <b>{o.days_since} hari</b>
              </div>
            ))}
        </div>
      </Modal>
    </div>
  );
}
